import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Home, Headphones, BookOpen, Users, User, Shield, LogOut } from "lucide-react";
import { useApp } from "../context/AppContext";
import { EmailSignup } from "./EmailSignup";
import AudioPlayer from "./AudioPlayer";
import { OFFICIAL_LOGO } from "../lib/brandAssets";

const NAV = [
  { to: "/listen", label: "Listen" },
  { to: "/learn", label: "Learn" },
  { to: "/read", label: "Read" },
  { to: "/gather", label: "Gather" },
  { to: "/store", label: "Store" },
  { to: "/about", label: "About" },
  { to: "/join", label: "Join" },
];

const MOBILE_TABS = [
  { to: "/", label: "Home", Icon: Home },
  { to: "/listen", label: "Listen", Icon: Headphones },
  { to: "/learn", label: "Learn", Icon: BookOpen },
  { to: "/gather", label: "Gather", Icon: Users },
  { to: "/my-temple", label: "Temple", Icon: User },
];

export default function Layout({ children }) {
  const { user, logout, currentTrack } = useApp();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isDashboard = location.pathname.startsWith("/my-temple") || location.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen flex flex-col relative" style={{ background: "var(--bg-cream)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md border-b"
        style={{ background: "rgba(247, 240, 227, 0.85)", borderColor: "var(--border-cream)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group min-w-0">
            <img src={OFFICIAL_LOGO} alt="Temple of the Human Heart" data-testid="header-official-logo" className="h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-lg flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="font-serif text-base sm:text-lg truncate" style={{ color: "var(--burgundy)" }}>
                Temple of the Human Heart
              </div>
              <div className="eyebrow -mt-0.5 hidden sm:block">Awaken · Re-Member · Return</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  "text-sm font-medium ink-link transition-colors " +
                  (isActive ? "text-[color:var(--burgundy)]" : "text-[color:var(--text-obsidian)] hover:text-[color:var(--burgundy)]")
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to={user.role === "ADMINISTRATOR" ? "/admin" : "/my-temple"}
                  data-testid="portal-link"
                  className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-colors"
                  style={{
                    background: "var(--burgundy)",
                    color: "var(--bg-cream)",
                    borderColor: "var(--burgundy)",
                  }}
                >
                  {user.role === "ADMINISTRATOR" ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  {user.role === "ADMINISTRATOR" ? "Admin" : "My Temple"}
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="hidden sm:inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="header-signin-link"
                className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border"
                style={{
                  background: "var(--burgundy)",
                  color: "var(--bg-cream)",
                  borderColor: "var(--burgundy)",
                }}
              >
                Sign in
              </Link>
            )}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t" style={{ borderColor: "var(--border-cream)" }}>
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}-link`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    "px-3 py-3 rounded-lg text-base font-medium " +
                    (isActive
                      ? "bg-[color:var(--burgundy-tint)] text-[color:var(--burgundy)]"
                      : "text-[color:var(--text-obsidian)]")
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {!user && (
                <Link
                  to="/login"
                  data-testid="mobile-signin-link"
                  onClick={() => setOpen(false)}
                  className="mt-2 px-3 py-3 rounded-lg text-base font-medium text-center"
                  style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
                >
                  Sign in / Join
                </Link>
              )}
              {user && (
                <>
                  <Link
                    to={user.role === "ADMINISTRATOR" ? "/admin" : "/my-temple"}
                    data-testid="mobile-portal-link"
                    onClick={() => setOpen(false)}
                    className="mt-2 px-3 py-3 rounded-lg text-base font-medium text-center"
                    style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
                  >
                    {user.role === "ADMINISTRATOR" ? "Admin Dashboard" : "My Temple"}
                  </Link>
                  <button
                    data-testid="mobile-logout-btn"
                    onClick={() => { logout(); setOpen(false); }}
                    className="px-3 py-3 rounded-lg text-base font-medium text-left"
                  >
                    Sign out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className={`flex-1 ${currentTrack ? "pb-40" : "pb-24"} lg:pb-0 lg:${currentTrack ? "pb-32" : ""}`}>
        {children}
      </main>

      {/* Footer */}
      {!isDashboard && (
        <footer
          className="mt-16 border-t"
          style={{ background: "var(--obsidian)", color: "var(--bg-cream)", borderColor: "var(--dark-card)" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={OFFICIAL_LOGO}
                  alt="Temple of the Human Heart"
                  data-testid="footer-official-logo"
                  className="h-14 w-14 rounded-md object-contain"
                  style={{ background: "var(--obsidian)" }}
                />
                <div className="font-serif text-2xl" style={{ color: "var(--gold)" }}>
                  Temple of the Human Heart
                </div>
              </div>
              <p className="text-sm leading-relaxed opacity-80 max-w-md">
                Love is the doctrine. Compassion is the practice. Awakening is the work.
                A contemplative sanctuary for the interior life.
              </p>
            </div>
            <div>
              <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>Pathways</div>
              <ul className="space-y-2 text-sm opacity-90">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:text-[color:var(--gold)]" data-testid={`footer-${n.label.toLowerCase()}-link`}>
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>Connect</div>
              <ul className="space-y-2 text-sm opacity-90">
                <li><a href="mailto:info@awakenandreturn.com" className="hover:text-[color:var(--gold)]" data-testid="footer-contact-email">info@awakenandreturn.com</a></li>
                <li>Atlanta, GA · Worldwide</li>
                <li><Link to="/about" className="hover:text-[color:var(--gold)]" data-testid="footer-about-link">About</Link></li>
                <li><Link to="/give" className="hover:text-[color:var(--gold)]" data-testid="footer-give-link">Support the Temple</Link></li>
                <li><Link to="/heart-requests" className="hover:text-[color:var(--gold)]" data-testid="footer-heart-requests-link">Heart Requests</Link></li>
                <li><Link to="/legal/privacy" className="hover:text-[color:var(--gold)]" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/legal/terms" className="hover:text-[color:var(--gold)]" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
                <li><Link to="/legal/refunds" className="hover:text-[color:var(--gold)]" data-testid="footer-refunds-link">Refund &amp; Return Policy</Link></li>
              </ul>
            </div>
            <div data-testid="footer-signup">
              <EmailSignup sourceDetail="footer" dark />
            </div>
          </div>
          <div className="border-t text-xs opacity-70 py-5 text-center" style={{ borderColor: "var(--dark-card)" }}>
            © 2026 Temple of the Human Heart · A sanctuary, not a doctrine
          </div>
        </footer>
      )}

      {/* Persistent audio */}
      <AudioPlayer />

      {/* Mobile bottom tab bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t backdrop-blur-md"
        style={{
          background: "rgba(247, 240, 227, 0.94)",
          borderColor: "var(--border-cream)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="grid grid-cols-5">
          {MOBILE_TABS.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`tabbar-${label.toLowerCase()}-link`}
              className={({ isActive }) =>
                "flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium tracking-wider uppercase " +
                (isActive ? "text-[color:var(--burgundy)]" : "text-[color:var(--text-muted)]")
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.2 : 1.6} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
